/**
 * Single source of truth (backend + frontend) for classifying an OFF-mode FBR
 * dry-run submission (T-297; originally T-252 / T-257 / T-283).
 *
 * OFF-mode submissions get a local dummy FBR invoice number `DI-OFF-…` (see the
 * fbr-api-client OFF branch, docs/contracts/fbr.contract.md §9). They are NOT real
 * FBR acceptances, so reporting/UI must never present or count them as `ACCEPTED`.
 *
 * Keep this file dependency-free (no Prisma / React) so both packages can import it.
 */
export const OFFLINE_DUMMY_PREFIX = 'DI-OFF-';

/** True when a submission's FBR invoice id is an OFF-mode dry-run dummy. */
export function isOfflineDummyInvoiceId(fbrInvoiceId: string | null | undefined): boolean {
  return typeof fbrInvoiceId === 'string' && fbrInvoiceId.startsWith(OFFLINE_DUMMY_PREFIX);
}

/** Prefix of the simulated id returned when STAGING/LIVE has no token (fbr-api-client simulateDiResponse). */
export const SIMULATED_PREFIX = 'SIM-';

/** True for any id that FBR did not issue (OFF-mode dummy or simulated). */
export function isNonFbrInvoiceId(fbrInvoiceId: string | null | undefined): boolean {
  return (
    isOfflineDummyInvoiceId(fbrInvoiceId) ||
    (typeof fbrInvoiceId === 'string' && fbrInvoiceId.startsWith(SIMULATED_PREFIX))
  );
}

/**
 * POS-I114: the FBR invoice number a customer-facing receipt may show (number + QR), or null.
 * Only when the tenant's FBR integration is enabled AND runs against the LIVE FBR environment
 * (staging ids are test ids) AND the id is a real FBR number (never `DI-OFF-*` / `SIM-*`).
 */
export function getPrintableFbrInvoiceId(input: {
  fbrInvoiceId?: string | null;
  fbrEnabled?: boolean | null;
  fbrEnvironment?: string | null;
}): string | null {
  const id = input.fbrInvoiceId?.trim();
  if (!id) return null;
  if (!input.fbrEnabled || input.fbrEnvironment !== 'LIVE') return null;
  if (isNonFbrInvoiceId(id)) return null;
  return id;
}
